// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';
import L from 'leaflet';
import 'L.MagnifyingGlass';

/**
 * Options for the Magnifying glass plugin
 * @typedef magnifyingGlassPluginOptions
 * @property {boolean}          [lockLayers=false]      Whether the layers should be locked, or track the layers of the main map
 * @property {string}           [position='topleft']    Position for the leaflet map control (button on map). If null (falsy), no button will be added
 * @property {integer}          [zoomOffset=1]          The zoom level offset between the main map zoom and the magnifying glass
 * @property {boolean}          [scrollWheelZoom=true]  Whether the mouse scroll wheel should control the zoom on the magnifying glass map or the zoom of the main map
 * @property {integer}          [radius=130]            The radius of the magnifying glass, in pixels
 * @property {Array<string>}    [excludeTags]           List of tags to exclude layers.
 *                                                  If a layer's description includes a tag, that layer will not be included in the magnifying glass's layers
 */

myw.MagnifyingGlassPlugin = myw.Plugin.extend({
    options: {
        enabled: !myw.isTouchDevice, //if false, the map control won't be instantiated or shown on the map
        lockLayers: false,
        position: 'topleft',
        zoomOffset: 1,
        scrollWheelZoom: true,
        radius: 130,
        excludeTags: []
    },

    /**
     * Creates the leaflet control and adds it to the application's map.
     * Options are passed on to the Leaflet control
     * @class   Plugin that adds a control to the map which displays a magnifying glass on the mouse position
     * @param  {Application}                owner       The application
     * @param  {magnifyingGlassPluginOptions}    options
     * @constructs
     * @extends {Plugin}
     */
    initialize(owner, options) {
        myw.Plugin.prototype.initialize.call(this, owner, options);

        this.setOptions(myw.config['core.plugin.magnifyingGlass']);

        myw.Util.loadStyleSheet(myw.baseUrl + 'lib/leaflet/plugins/leaflet.magnifyingglass.css');

        if (this.options.enabled && !this.app.isHandheld) {
            this.enable();
        } else {
            this.enabled = false;
        }
    },

    /**
     * Adds the magnifying glass map control
     */
    enable() {
        this.map = this.app.map;

        this.featureReps = [];

        if (this.options.position) {
            //add a leaflet map control that activates the functionality
            const ctlOptions = _.extend({ magnifier: this }, this.options);
            this.control = new myw.MagnifyingGlassControl(ctlOptions);
            this.control.addTo(this.map);
        }

        // Register keyboard handlers
        // Close magnifier when Esc is pressed, Crtl-M opens magnifier
        $(document).keyup(e => {
            if (e.keyCode == 27) {
                if (this.map.hasLayer(this.magnifyingGlass)) {
                    this.map.removeLayer(this.magnifyingGlass);
                    this.map.scrollWheelZoom.enable();
                }
            } else if (e.keyCode == 77 && e.ctrlKey) {
                if (!this.map.hasLayer(this.magnifyingGlass)) {
                    this.addMagnifyingGlass();
                    this.magnifyingGlass._update(this.map.getCenter());
                }
            }
        });

        if (!this.options.lockLayers) {
            //Listen to Layers Control checked/unchecked event, and refresh magnifier
            this.app.on('overlayState-changed', ev => {
                if (this.map.hasLayer(this.magnifyingGlass) && ev.map == this.map) {
                    const latLng = this.magnifyingGlass.options.latLng;
                    //Refresh magnifier by reloading
                    this.map.removeLayer(this.magnifyingGlass);
                    this.addMagnifyingGlass();
                    this.magnifyingGlass._update(latLng);
                }
            });
        }

        this.enabled = true;
    },

    /**
     * Removes the magnifying glass map control
     */
    disable() {
        this.control.remove();
        this.control = null;
        this.enabled = false;
        this.removeMagnifyingGlass();
    },

    /**
     * Toggle magnifying glass mode
     */
    toggle() {
        if (this.map.hasLayer(this.magnifyingGlass)) {
            this.removeMagnifyingGlass();
        } else {
            //Launch magnifier
            this.addMagnifyingGlass();
        }
    },

    /**
     * Removes the magnifying glass layer from the map
     */
    removeMagnifyingGlass() {
        if (this.map.hasLayer(this.magnifyingGlass)) {
            this.map.removeLayer(this.magnifyingGlass);
        }
        this.map.scrollWheelZoom.enable();
    },

    /**
     * Add magnifier to map
     */
    addMagnifyingGlass() {
        const app = this.app;
        this.layerIds = [];

        if (this.options.scrollWheelZoom) {
            //Disable mouse zoom in main map as we want the zoom to only affect the magnifying glass map
            app.map.scrollWheelZoom.disable();
        }

        //Create magnifier
        this.magnifyingGlass = new L.MagnifyingGlass(
            {
                zoomOffset: this.options.zoomOffset,
                radius: this.options.radius,
                scrollWheelZoom: this.options.scrollWheelZoom
            },
            app
        );

        this.map.addLayer(this.magnifyingGlass);

        const glassMap = this.magnifyingGlass.getMap();
        glassMap.app = app;
        //Create a layerManager for magnifier map, so that the vector layer created by the layer manager will work.
        this.glassLayerManager = new myw.LayerManager(glassMap);

        //Get basemap
        const baseLayerDef = _.findWhere(app.map.baseLayerDefs, {
            name: app.map._currentBaseMapName
        });
        const datasource = this.app.database.getDatasource(baseLayerDef.datasource);
        const baseLayer = new myw.Layer(datasource, baseLayerDef);
        glassMap.addLayer(baseLayer);

        //Get overlays
        const mapLayers = app.map.layerManager.layers;
        _.each(mapLayers, layer => {
            if (layer.isChecked) {
                const exclude = _.any(
                    this.options.excludeTags,
                    tag => layer.layerDef.description.indexOf(tag) >= 0
                );
                if (!exclude) {
                    const mlayer = this.glassLayerManager.createLayer(layer.layerDef);
                    mlayer.setVisibility(true);

                    const code = layer.getCode();
                    this.layerIds.push(code);
                }
            }
        });

        //Show selection
        this.highlight(app.currentFeatureSet.items);

        //Listen to magnifier click event and select features
        this.magnifyingGlass.on('click', this.handleMapClick.bind(this));
    },

    /**
     * Magnifier click event handler
     * (This is a variation of application.handleMapClick due to the difference in zoom level for the magnifier,
     * also have to highlight the features in the magnifier.)
     * select features in magnifier
     */
    handleMapClick() {
        const that = this;
        const latLng = this.magnifyingGlass.getMap().getCenter();

        //notify that a selection has started (ex: so featureDetails can show an appropriate message)
        this.app.fire('selection-started', { origin: 'map' });

        //SelectFeatures in custom geoMapControl is overriden to take zoom level
        this.app.map.selectFeatures(latLng, this.magnifyingGlass._getZoom()).then(features => {
            _.last(that.map._interactionModeStack).processSelectedFeatures(features);
            that.highlight(features);
        });
    },

    /**
     * Returns object with current state details <br/>
     * @return {object}
     * @param {boolean} state.enabled Whether the controls is on the map or not
     */
    getState() {
        return {
            enabled: this.enabled
        };
    },

    /**
     * Highligh features in magnifier
     * @param  {Array<Feature>} features myWorld features to highlight
     */
    highlight(features) {
        const glassMap = this.magnifyingGlass.getMap();

        //remove previous highlights
        _.each(
            this.featureReps,
            featureRep => {
                featureRep.removeFromMap(glassMap);
            },
            this
        );
        this.featureReps = [];

        _.each(features, feature => {
            if (feature.geometry) {
                const featureRep = new myw.FeatureRepresentation(feature);
                featureRep.addToMap(glassMap);
                featureRep.highlight();
                this.featureReps.push(featureRep);
            }
        });

        if (this.featureReps.length > 1) {
            _.each(this.featureReps, featureRep => {
                featureRep.unHighlight();
            });
        }
    },

    /**
     * Toggles enabled state of the plugin.
     * If the control is on the map, removes it.
     * If the control is not on the map, adds it
     * @return {boolean} Whether the control is now enabled or not
     */
    toggleControl() {
        const wasEnabled = this.enabled;
        if (wasEnabled) {
            this.disable();
        } else {
            this.enable();
        }
        return !wasEnabled;
    },

    /**
     * Actions to be used by map context menu
     */
    actions: {
        toggle: {
            //unavailable on mobile devices
            //ENH: detect devices with mouses
            available: !myw.isTouchDevice,
            action: 'toggleControl',
            checked: 'enabled'
        }
    }
});

/*
 * Magnifier plugin for myWorld layers and selection
 */
myw.MagnifyingGlassControl = L.Control.extend({
    options: {
        position: 'topleft'
    },

    /*
     * Callback handler when magnifier control is being added to the map
     * @param  {L.Map} map main map
     */
    onAdd(map) {
        const magnifier = this.options.magnifier;

        //Add the magnifier button to the left control bar
        this._container = L.DomUtil.create('div', 'leaflet-bar-part leaflet-bar');
        const link = L.DomUtil.create('a', 'leaflet-control-magnifying-glass', this._container);
        link.href = '#';
        link.title = myw.msg('MagnifyingGlassPlugin', 'toggle');

        //Listen to click event to launch/close the magnifier
        L.DomEvent.addListener(link, 'click', L.DomEvent.stopPropagation)
            .addListener(link, 'click', L.DomEvent.preventDefault)
            .addListener(link, 'click', magnifier.toggle, magnifier);

        return this._container;
    }
});

export default myw.MagnifyingGlassPlugin;
